import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, AlertOctagon, Siren } from "lucide-react";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import GpsCard from "@/components/GpsCard";
import FeuButton from "@/components/FeuButton";
import NotificationBanner from "@/components/NotificationBanner";
import OrangeConfigDialog from "@/components/OrangeConfigDialog";
import RedConfirmDialog from "@/components/RedConfirmDialog";
import useGeolocation from "@/hooks/useGeolocation";
import { createAlert, reverseGeocode } from "@/lib/alerts";
import { formatApiError } from "@/lib/api";
import { ensurePushSubscriptionIfGranted } from "@/lib/push";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Home() {
  const navigate = useNavigate();

  const geocoder = useCallback(async (lat, lng) => {
    try {
      const res = await reverseGeocode(lat, lng);
      return res.address;
    } catch (_e) {
      return null;
    }
  }, []);
  const geo = useGeolocation({ reverseGeocode: geocoder });

  const [clientName, setClientName] = useState("");
  const [rdvAddress, setRdvAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orangeOpen, setOrangeOpen] = useState(false);
  const [redOpen, setRedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Re-subscribe silently if permission was already granted.
    ensurePushSubscriptionIfGranted();
  }, []);

  // Suggest the GPS address the first time it resolves, unless user already typed something
  const [gpsAutofilled, setGpsAutofilled] = useState(false);
  if (geo.address && !gpsAutofilled && !rdvAddress) {
    setRdvAddress(geo.address);
    setGpsAutofilled(true);
  }

  const usableAddress = rdvAddress.trim() || geo.address || "";
  const usableLocation = useMemo(
    () => (geo.coords ? { lat: geo.coords.lat, lng: geo.coords.lng } : null),
    [geo.coords]
  );

  const canTrigger = useMemo(
    () => Boolean(usableAddress || usableLocation),
    [usableAddress, usableLocation]
  );

  const handleGreenClick = () => {
    if (!canTrigger) {
      toast.error("Adresse ou position GPS requise pour déclencher une alerte.");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmGreen = async () => {
    setSubmitting(true);
    try {
      const alert = await createAlert({
        level: "green",
        address: usableAddress || null,
        location: usableLocation,
        client_name: clientName || null,
        notes: notes || null,
      });
      toast.success("Feu vert enregistré. L'équipe peut vous suivre discrètement.");
      setConfirmOpen(false);
      setClientName("");
      setNotes("");
      navigate(`/alert/${alert.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showComingSoon = () =>
    toast("Disponible prochainement", {
      description: "Cette action sera activée dans une prochaine phase.",
    });

  const handleRedClick = () => {
    if (!canTrigger) {
      toast.error("Adresse ou position GPS requise pour déclencher une urgence.");
      return;
    }
    setRedOpen(true);
  };

  const handleConfirmRed = async () => {
    setSubmitting(true);
    try {
      const alert = await createAlert({
        level: "red",
        address: usableAddress || null,
        location: usableLocation,
        client_name: clientName || null,
        notes: notes || null,
      });
      toast.success("Urgence déclenchée. L'équipe est alertée.");
      setRedOpen(false);
      setClientName("");
      setNotes("");
      navigate(`/alert/${alert.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOrangeClick = () => {
    if (!canTrigger) {
      toast.error("Adresse ou position GPS requise pour déclencher une alerte.");
      return;
    }
    setOrangeOpen(true);
  };

  const handleConfirmOrange = async (delayMinutes) => {
    setSubmitting(true);
    try {
      const alert = await createAlert({
        level: "orange",
        address: usableAddress || null,
        location: usableLocation,
        client_name: clientName || null,
        notes: notes || null,
        reminder_delay_minutes: delayMinutes,
      });
      toast.success("Feu orange déclenché. L'équipe est prévenue.");
      setOrangeOpen(false);
      setClientName("");
      setNotes("");
      navigate(`/alert/${alert.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--sa-subtle)] pb-24" data-testid="home-screen">
      <AppTopbar />

      <main className="mx-auto max-w-xl px-4 pt-5">
        <NotificationBanner />
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <GpsCard state={geo} onRefresh={geo.refresh} />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="sa-card mt-4 px-5 py-5"
        >
          <h2
            className="text-[13px] font-semibold uppercase tracking-wider text-slate-500"
          >
            Détails du rendez-vous
          </h2>
          <div className="mt-3 flex flex-col gap-4">
            <div>
              <label className="sa-label" htmlFor="rdv-client">
                Nom du client (optionnel)
              </label>
              <input
                id="rdv-client"
                data-testid="rdv-client-input"
                type="text"
                className="sa-input"
                placeholder="Mme Dupont, visite 3 pièces…"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                maxLength={200}
              />
            </div>
            <div>
              <label className="sa-label" htmlFor="rdv-address">
                Adresse du rendez-vous
              </label>
              <textarea
                id="rdv-address"
                data-testid="rdv-address-input"
                className="sa-input"
                style={{ height: 68, padding: "12px 16px", resize: "vertical" }}
                placeholder="Pré-remplie par votre GPS, éditable si besoin"
                value={rdvAddress}
                onChange={(e) => setRdvAddress(e.target.value)}
                maxLength={500}
              />
            </div>
            <div>
              <label className="sa-label" htmlFor="rdv-notes">
                Notes (optionnel)
              </label>
              <textarea
                id="rdv-notes"
                data-testid="rdv-notes-input"
                className="sa-input"
                style={{ height: 68, padding: "12px 16px", resize: "vertical" }}
                placeholder="Ex: RDV prévu 45 min, client rencontré il y a 2 semaines"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
              />
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 flex flex-col gap-3"
          data-testid="feu-buttons"
        >
          <FeuButton
            level="green"
            title="RDV normal"
            subtitle="Suivi discret — l'équipe sait que tout va bien"
            icon={CheckCircle2}
            onClick={handleGreenClick}
            disabled={!canTrigger}
            loading={submitting}
            testId="feu-green-button"
          />
          <FeuButton
            level="orange"
            title="À surveiller"
            subtitle="Rappel programmé — l'équipe est prévenue sans stress"
            icon={AlertOctagon}
            onClick={handleOrangeClick}
            disabled={!canTrigger}
            testId="feu-orange-button"
          />
          <FeuButton
            level="red"
            title="URGENCE"
            subtitle="Alerte équipe immédiate — tous les contacts notifiés"
            icon={Siren}
            onClick={handleRedClick}
            disabled={!canTrigger}
            testId="feu-red-button"
          />
        </motion.section>

        <p className="mt-6 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>

      <BottomNav />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="max-w-[420px] rounded-[20px]"
          data-testid="confirm-green-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-slate-900">
              Confirmer le feu vert ?
            </DialogTitle>
            <DialogDescription className="mt-1 text-[14px] text-slate-500">
              L'équipe pourra vous suivre discrètement. Aucune notification ne
              sera envoyée à vos contacts d'urgence.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 rounded-[14px] border border-slate-200 bg-[var(--sa-subtle)] p-4 text-[13px]">
            <p className="font-semibold text-slate-700">Adresse</p>
            <p className="mt-0.5 text-slate-600">{usableAddress || "—"}</p>
            {clientName ? (
              <>
                <p className="mt-3 font-semibold text-slate-700">Client</p>
                <p className="mt-0.5 text-slate-600">{clientName}</p>
              </>
            ) : null}
          </div>

          <DialogFooter className="mt-2 flex gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              data-testid="confirm-green-cancel"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirmGreen}
              disabled={submitting}
              className="flex-1 rounded-[14px] px-5 py-3 text-[14px] font-semibold text-white transition disabled:opacity-70"
              style={{
                background: "#22C55E",
                boxShadow: "0 4px 14px -4px rgba(34,197,94,0.5)",
              }}
              data-testid="confirm-green-submit"
            >
              {submitting ? "Enregistrement…" : "Confirmer le feu vert"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrangeConfigDialog
        open={orangeOpen}
        onOpenChange={setOrangeOpen}
        address={usableAddress}
        clientName={clientName}
        submitting={submitting}
        onSubmit={handleConfirmOrange}
      />

      <RedConfirmDialog
        open={redOpen}
        onOpenChange={setRedOpen}
        address={usableAddress}
        clientName={clientName}
        submitting={submitting}
        onSubmit={handleConfirmRed}
      />
    </div>
  );
}
